import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useReducer,
  Dispatch,
} from 'react';
import { useAcademicsSchedulesHook } from '../hooks/useAcademicsSchedulesHook';
import {
  IAcademicsScheduleResponse,
  IAcademicsSchedule,
  EScheduleType,
  EWeekDay,
  ICompositeScheduleView,
  ITeacherSchedule,
  IStudentSchedule,
  IClassSchedule,
} from '../../../../apis/academics/academicsSchedules.interfaces';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { UseQueryResult } from '@tanstack/react-query';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useAcademicsFiltersContext } from './academicsFilters.context';

type TQueryError = IFHError<{ message: string }>;

export enum ACTION_TYPE {
  SET_IS_MANAGE_SCHEDULE_OPEN = 'SET_IS_MANAGE_SCHEDULE_OPEN',
  SET_SELECTED_SCHEDULE = 'SET_SELECTED_SCHEDULE',
  SET_SELECTED_SCHEDULE_ID = 'SET_SELECTED_SCHEDULE_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_SCHEDULE_LOADING = 'SET_IS_SCHEDULE_LOADING',
  SET_IS_SCHEDULE_ERROR = 'SET_IS_SCHEDULE_ERROR',
  SET_SCHEDULE_ERROR_MESSAGE = 'SET_SCHEDULE_ERROR_MESSAGE',
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
}

type TAcademicsSchedulesAppState = {
  isManageScheduleOpen: boolean;
  isScheduleLoading: boolean;
  isScheduleError: boolean;
  scheduleErrorMessage: string;
  selectedSchedule: IAcademicsSchedule | null;
  selectedScheduleId: string | null;
  isCreateMode: boolean;
  isEditMode: boolean;
  userParams: TPaginationParams;
};

type Action =
  | { type: ACTION_TYPE.SET_IS_MANAGE_SCHEDULE_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_SCHEDULE_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_SCHEDULE_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_SCHEDULE_ERROR_MESSAGE; payload: string }
  | {
      type: ACTION_TYPE.SET_SELECTED_SCHEDULE;
      payload: IAcademicsSchedule | null;
    }
  | { type: ACTION_TYPE.SET_SELECTED_SCHEDULE_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams };

const initialAcademicsSchedulesState: TAcademicsSchedulesAppState = {
  isManageScheduleOpen: false,
  isScheduleLoading: false,
  isScheduleError: false,
  scheduleErrorMessage: '',
  selectedSchedule: null,
  selectedScheduleId: null,
  isCreateMode: false,
  isEditMode: false,
  userParams: {
    page: 1,
    limit: 10,
  },
};

interface TAcademicsSchedulesState {
  // App state
  academicsSchedulesAppState: TAcademicsSchedulesAppState;

  // Queries
  schedulesQuery: UseQueryResult<IAcademicsScheduleResponse, TQueryError>;

  // Refetch functions
  refetchSchedules: () => void;

  // Schedule utility functions
  getScheduleById: (id: string) => IAcademicsSchedule | undefined;
  getSchedulesByType: (type: EScheduleType) => IAcademicsSchedule[];
  getActiveSchedules: () => IAcademicsSchedule[];

  // View generation functions
  generateTeacherSchedule: (
    teacherId: number,
    teacherName: string
  ) => ITeacherSchedule | null;
  generateStudentSchedule: (
    studentId: string,
    studentName: string,
    classId: number,
    className: string,
    section?: string
  ) => IStudentSchedule | null;
  generateClassSchedule: (
    classId: number,
    className: string,
    section?: string
  ) => IClassSchedule | null;
  generateCompositeView: (viewParams: {
    scheduleId: string;
    scheduleName: string;
    viewType: string;
    referenceId: string | number;
    referenceName: string;
  }) => ICompositeScheduleView | null;

  // Helper functions
  getSchedulesForDay: (
    scheduleId: string,
    day: EWeekDay
  ) => IAcademicsSchedule | null;

  // Loading and error states
  isLoading: boolean;
  isError: boolean;
  error: TQueryError | null;
}

interface AcademicsSchedulesProviderProps {
  children: ReactNode;
  paginationParams?: TPaginationParams;
}

const AcademicsSchedulesContext = createContext<
  TAcademicsSchedulesState | undefined
>(undefined);

const AcademicsSchedulesDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const academicsSchedulesReducer = (
  state: TAcademicsSchedulesAppState,
  action: Action
): TAcademicsSchedulesAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        userParams: { ...state.userParams, ...action.payload },
      };
    case ACTION_TYPE.SET_IS_MANAGE_SCHEDULE_OPEN:
      return { ...state, isManageScheduleOpen: action.payload };
    case ACTION_TYPE.SET_IS_SCHEDULE_LOADING:
      return { ...state, isScheduleLoading: action.payload };
    case ACTION_TYPE.SET_IS_SCHEDULE_ERROR:
      return { ...state, isScheduleError: action.payload };
    case ACTION_TYPE.SET_SCHEDULE_ERROR_MESSAGE:
      return { ...state, scheduleErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_SCHEDULE:
      return { ...state, selectedSchedule: action.payload };
    case ACTION_TYPE.SET_SELECTED_SCHEDULE_ID:
      return { ...state, selectedScheduleId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };
    default:
      return state;
  }
};

export const AcademicsSchedulesProvider: React.FC<
  AcademicsSchedulesProviderProps
> = ({ children, paginationParams = { page: 1, limit: 10 } }) => {
  const [appState, dispatch] = useReducer(
    academicsSchedulesReducer,
    initialAcademicsSchedulesState
  );

  const { academicYear, grade } = useAcademicsFiltersContext();

  const safeParams =
    appState.userParams.page && appState.userParams.limit
      ? appState.userParams
      : { page: paginationParams.page ?? 1, limit: paginationParams.limit ?? 10 };
  const schedulesQuery = useAcademicsSchedulesHook({
    ...safeParams,
    academicYear,
    grade,
  });

  const refetchSchedules = useCallback(() => {
    schedulesQuery.refetch();
  }, [schedulesQuery]);

  // Schedule utility functions
  const getScheduleById = useCallback(
    (id: string): IAcademicsSchedule | undefined => {
      return schedulesQuery.data?.schedules.find(
        (schedule) => schedule.id === id
      );
    },
    [schedulesQuery]
  );

  const getSchedulesByType = useCallback(
    (type: EScheduleType): IAcademicsSchedule[] => {
      if (!schedulesQuery.data) return [];
      return schedulesQuery.data.schedules.filter(
        (schedule) => schedule.type === type
      );
    },
    [schedulesQuery]
  );

  const getActiveSchedules = useCallback((): IAcademicsSchedule[] => {
    if (!schedulesQuery.data) return [];
    return schedulesQuery.data.schedules.filter(
      (schedule) => schedule.isActive
    );
  }, [schedulesQuery]);

  // View generation functions
  const generateTeacherSchedule = useCallback(
    (teacherId: number, teacherName: string): ITeacherSchedule | null => {
      if (!schedulesQuery.data) return null;

      // Find all periods assigned to this teacher
      const teacherSchedules = schedulesQuery.data.schedules
        .filter((schedule) => schedule.isActive)
        .flatMap((schedule) =>
          schedule.periods.filter((period) => period.teacherId === teacherId)
        );

      if (teacherSchedules.length === 0) return null;

      // Group by day of week
      const schedulesByDay = Object.values(EWeekDay).reduce(
        (acc, day) => {
          const dayPeriods = teacherSchedules.filter(
            (period) => period.dayOfWeek === day
          );

          if (dayPeriods.length > 0) {
            acc.push({
              day,
              periods: dayPeriods.sort(
                (a, b) => a.periodNumber - b.periodNumber
              ),
              totalPeriods: dayPeriods.length,
            });
          }

          return acc;
        },
        [] as ITeacherSchedule['schedules']
      );

      return {
        teacherId,
        teacherName,
        schedules: schedulesByDay,
      };
    },
    [schedulesQuery]
  );

  const generateStudentSchedule = useCallback(
    (
      studentId: string,
      studentName: string,
      classId: number,
      className: string,
      section?: string
    ): IStudentSchedule | null => {
      if (!schedulesQuery.data) return null;

      // Find all periods assigned to this class
      const studentSchedules = schedulesQuery.data.schedules
        .filter((schedule) => schedule.isActive)
        .flatMap((schedule) =>
          schedule.periods.filter((period) => period.classId === classId)
        );

      if (studentSchedules.length === 0) return null;

      // Group by day of week
      const schedulesByDay = Object.values(EWeekDay).reduce(
        (acc, day) => {
          const dayPeriods = studentSchedules.filter(
            (period) => period.dayOfWeek === day
          );

          if (dayPeriods.length > 0) {
            acc.push({
              day,
              periods: dayPeriods.sort(
                (a, b) => a.periodNumber - b.periodNumber
              ),
            });
          }

          return acc;
        },
        [] as IStudentSchedule['schedules']
      );

      return {
        studentId,
        studentName,
        classId,
        className,
        section,
        schedules: schedulesByDay,
      };
    },
    [schedulesQuery]
  );

  const generateClassSchedule = useCallback(
    (
      classId: number,
      className: string,
      section?: string
    ): IClassSchedule | null => {
      if (!schedulesQuery.data) return null;

      // Find all periods assigned to this class
      const classSchedules = schedulesQuery.data.schedules
        .filter((schedule) => schedule.isActive)
        .flatMap((schedule) =>
          schedule.periods.filter((period) => period.classId === classId)
        );

      if (classSchedules.length === 0) return null;

      // Group by day of week
      const schedulesByDay = Object.values(EWeekDay).reduce(
        (acc, day) => {
          const dayPeriods = classSchedules.filter(
            (period) => period.dayOfWeek === day
          );

          if (dayPeriods.length > 0) {
            acc.push({
              day,
              periods: dayPeriods.sort(
                (a, b) => a.periodNumber - b.periodNumber
              ),
            });
          }

          return acc;
        },
        [] as IClassSchedule['schedules']
      );

      return {
        classId,
        className,
        section,
        schedules: schedulesByDay,
      };
    },
    [schedulesQuery]
  );

  const generateCompositeView = useCallback(
    (viewParams: {
      scheduleId: string;
      scheduleName: string;
      viewType: string;
      referenceId: string | number;
      referenceName: string;
    }): ICompositeScheduleView | null => {
      if (!schedulesQuery.data) return null;

      const schedule = schedulesQuery.data.schedules.find(
        (s) => s.id === viewParams.scheduleId
      );
      if (!schedule) return null;

      // Process schedules based on the view type
      let filteredPeriods = schedule.periods;

      // Filter periods based on view type
      if (viewParams.viewType === 'Teacher') {
        filteredPeriods = schedule.periods.filter(
          (p) => p.teacherId?.toString() === viewParams.referenceId.toString()
        );
      } else if (viewParams.viewType === 'Class') {
        filteredPeriods = schedule.periods.filter(
          (p) => p.classId?.toString() === viewParams.referenceId.toString()
        );
      } else if (viewParams.viewType === 'Room') {
        filteredPeriods = schedule.periods.filter(
          (p) => p.room === viewParams.referenceId
        );
      }

      // Group by day
      const scheduleDays = Object.values(EWeekDay).reduce(
        (acc, day) => {
          const dayPeriods = filteredPeriods.filter(
            (period) => period.dayOfWeek === day
          );

          if (dayPeriods.length > 0) {
            acc.push({
              day,
              periods: dayPeriods.sort(
                (a, b) => a.periodNumber - b.periodNumber
              ),
            });
          }

          return acc;
        },
        [] as ICompositeScheduleView['days']
      );

      return {
        scheduleId: viewParams.scheduleId,
        scheduleName: viewParams.scheduleName,
        viewType: viewParams.viewType as any,
        referenceId: viewParams.referenceId,
        referenceName: viewParams.referenceName,
        days: scheduleDays,
      };
    },
    [schedulesQuery]
  );

  // Helper functions
  const getSchedulesForDay = useCallback(
    (scheduleId: string, day: EWeekDay): IAcademicsSchedule | null => {
      if (!schedulesQuery.data) return null;

      const schedule = schedulesQuery.data.schedules.find(
        (s) => s.id === scheduleId
      );
      if (!schedule) return null;

      const filteredSchedule = {
        ...schedule,
        periods: schedule.periods
          .filter((period) => period.dayOfWeek === day)
          .sort((a, b) => a.periodNumber - b.periodNumber),
      };

      return filteredSchedule.periods.length > 0 ? filteredSchedule : null;
    },
    [schedulesQuery]
  );

  // Loading and error states
  const isLoading = schedulesQuery.isLoading;
  const isError = schedulesQuery.isError;
  const error = schedulesQuery.error || null;

  const value = useMemo(
    () => ({
      // App state
      academicsSchedulesAppState: appState,

      // Queries
      schedulesQuery,

      // Refetch functions
      refetchSchedules,

      // Schedule utility functions
      getScheduleById,
      getSchedulesByType,
      getActiveSchedules,

      // View generation functions
      generateTeacherSchedule,
      generateStudentSchedule,
      generateClassSchedule,
      generateCompositeView,

      // Helper functions
      getSchedulesForDay,

      // Loading and error states
      isLoading,
      isError,
      error,
    }),
    [
      appState,
      schedulesQuery,
      refetchSchedules,
      getScheduleById,
      getSchedulesByType,
      getActiveSchedules,
      generateTeacherSchedule,
      generateStudentSchedule,
      generateClassSchedule,
      generateCompositeView,
      getSchedulesForDay,
      isLoading,
      isError,
      error,
    ]
  );

  return (
    <AcademicsSchedulesContext.Provider value={value}>
      <AcademicsSchedulesDispatchContext.Provider value={dispatch}>
        {children}
      </AcademicsSchedulesDispatchContext.Provider>
    </AcademicsSchedulesContext.Provider>
  );
};

export const useAcademicsSchedulesContext = (): TAcademicsSchedulesState => {
  const context = useContext(AcademicsSchedulesContext);

  if (context === undefined) {
    throw new Error(
      'useAcademicsSchedulesContext must be used within a AcademicsSchedulesProvider'
    );
  }

  return context;
};

export const useAcademicsSchedulesDispatch = () => {
  const context = useContext(AcademicsSchedulesDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useAcademicsSchedulesDispatch must be used within a AcademicsSchedulesProvider'
    );
  }
  return context;
};

export const useAcademicsSchedulesActions = () => {
  const dispatch = useAcademicsSchedulesDispatch();
  if (dispatch === undefined) {
    throw new Error(
      'useAcademicsSchedulesActions must be used within a AcademicsSchedulesProvider'
    );
  }

  const setIsManageScheduleOpen = (isOpen: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MANAGE_SCHEDULE_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedSchedule = (schedule: IAcademicsSchedule | null) => {
    dispatch?.({ type: ACTION_TYPE.SET_SELECTED_SCHEDULE, payload: schedule });
  };

  const setIsScheduleLoading = (isLoading: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_SCHEDULE_LOADING,
      payload: isLoading,
    });
  };

  const setIsScheduleError = (isError: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_SCHEDULE_ERROR, payload: isError });
  };

  const setScheduleErrorMessage = (errorMessage: string) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SCHEDULE_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedScheduleId = (scheduleId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_SCHEDULE_ID,
      payload: scheduleId,
    });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  return {
    setIsManageScheduleOpen,
    setSelectedSchedule,
    setIsScheduleLoading,
    setIsScheduleError,
    setScheduleErrorMessage,
    setSelectedScheduleId,
    setIsCreateMode,
    setIsEditMode,
  };
};
